import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch, numberFormat, toBuddhistYear } from 'utils/app.utils'

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import IncreaseList from './IncreaseList'

/**STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import { dateToView, timeToView } from 'utils/date.utils'
import { UseSnCode } from 'utils/useSnCode'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function AdvanceIncrease() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)

  const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [search, setSearch] = useState<string>('')
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [billStatus, setBillStatus] = useState<string>('ALL')
  const [channel, setChannel] = useState<string>('ALL')

  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [fetch, setFetch] = useState(false)
  const [rows, setRows] = useState<any[]>([])
  const [rowsExport, setRowsExport] = useState<any[]>([])
  const [rowsSummary, setRowsSummary] = useState<any[]>([])
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(true)
  const [snType, setSnType] = useState(false)

  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode()
      setSnType(sn)
    }
    fetchData()
  }, [snType])

  useEffect(() => {
    handleSnCode()
  }, [])

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'ADVANCE_INCREASE') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })

    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [branchId])

  const fetchDF = useCallback(async () => {
    let condition: any = {
      page,
      pageLimit,
      branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      search
    }

    if (channel && channel !== 'ALL') condition = { ...condition, channel: channel }
    if (billStatus && billStatus !== 'ALL') condition = { ...condition, billStatus: billStatus }

    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    ReportApi.reportAdvanceIncrease(condition)
      .then(({ headers, data }) => {
        setRows(data.rows)
        setRowsExport(data.rowsExport)
        setRowsSummary(data.summary)
        setRowCount(headers['x-total'])
      })
      .catch((e) => setLoading(false))
      .finally(() => {
        setLoading(false)
      })
    setFetch(false)
  }, [page, pageLimit, sortBy, sortType, fetch])

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.ADVANCE_INCREASE`)}`,
    sheetName: `${t(`REPORT.REPORT_NAME.ADVANCE_INCREASE`)}`
  })

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

  const handleChangePage = (val: number) => setPage(val)

  useEffect(() => {
    fetchDF()
  }, [fetchDF])

  const branchName = _.get(_.find(branches, { branchId }), 'branchName') || _.get(_.find(branches, { branchId }), 'branchNameEn')

  const sumChannel1 = _.find(rowsSummary, { paymentChannelId: 1 })
  const sumChannel2 = _.find(rowsSummary, { paymentChannelId: 2 })
  const sumChannel3 = _.find(rowsSummary, { paymentChannelId: 3 })
  const sumChannel4 = _.find(rowsSummary, { paymentChannelId: 4 })
  const summaryRows = [
    { labelKey: 'REPORT.PAYMENT_CHANNEL.1', total: sumChannel1?.amountTotal || 0, cancel: sumChannel1?.amountCancel || 0 },
    { labelKey: 'REPORT.PAYMENT_CHANNEL.2', total: sumChannel2?.amountTotal || 0, cancel: sumChannel2?.amountCancel || 0 },
    { labelKey: 'REPORT.PAYMENT_CHANNEL.3', total: sumChannel3?.amountTotal || 0, cancel: sumChannel3?.amountCancel || 0 },
    { labelKey: 'REPORT.PAYMENT_CHANNEL.4', total: sumChannel4?.amountTotal || 0, cancel: sumChannel4?.amountCancel || 0 },
  ]
  const sumTotal = _.sumBy(summaryRows, 'total')
  const sumCancel = _.sumBy(summaryRows, 'cancel')

  return (
    <Box>
      <HeaderCard text={t(`REPORT.REPORT_NAME.ADVANCE_INCREASE`)} />
      <Box px={3} pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('INPUT.SEARCH_PATIENT')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => setBranchId(Number(e.target.value))}
              options={[
                ...branches.map(b => ({ value: b.branchId, name: b.branchName || b.branchNameEn }))
              ]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              label={''}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.TABLE.INCREASE.CHANNEL_INCREASE')}
              value={channel}
              onChange={e => {
                setChannel(e.target.value as string)
                setPage(1)
              }}
              options={[
                { value: 'ALL', name: t('REPORT.FILTER.ALL') },
                { value: '1', name: t('REPORT.PAYMENT_CHANNEL.1') },
                { value: '2', name: t('REPORT.PAYMENT_CHANNEL.2') },
                { value: '3', name: t('REPORT.PAYMENT_CHANNEL.3') },
                { value: '4', name: t('REPORT.PAYMENT_CHANNEL.4') },
              ]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.RECEIPT_STATUS')}
              value={billStatus}
              onChange={e => {
                setBillStatus(e.target.value as string)
                setPage(1)
              }}
              options={[
                { value: 'ALL', name: t('REPORT.FILTER.ALL') },
                { value: 'NORMAL', name: t('REPORT.FILTER.BILL.NORMAL') },
                { value: 'CANCEL', name: t('REPORT.FILTER.BILL.CANCEL') },
              ]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1} xl={1}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type="primary"
              variant="contained"
              onClick={() => {
                setFetch(true)
                setPage(1)
                setLoading(true)
              }}
              style={{ width: '120px' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8} xl={1} display="flex" justifyContent="flex-end">
            <DropdownButton
              title={t('REGISTER.BUTTON.PRINT_DOCUMENT')}
              onChange={(e) => {
                if (e === 'Excel') onDownload()
                else if (e === 'PDF') {
                  const style = document.createElement('style')
                  style.innerHTML = `
                      @media print {
                        @page { size: landscape }
                      }
                    `
                  document.head.appendChild(style)
                  window.print()
                  document.head.removeChild(style)
                }
              }}
              options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]}
            />
          </Grid>
        </Grid>
        <Box className={'mt-3 pl-xl- pr-xl-2 pb-4'}>
          <IncreaseList
            page={page}
            pageLimit={pageLimit}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={rows}
            summary={rowsSummary}
            dataExport={rowsExport}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            setSortType={setSortType}
            setSortBy={setSortBy}
          />
        </Box>
      </Box>

      <Box id="print-table-container" ref={componentRef}>
        <Typography fontSize="14px" fontWeight={500}>{t(`REPORT.REPORT_NAME.ADVANCE_INCREASE`)}</Typography>
        <Box display="flex" flexDirection="row" alignItems="center" gap="16px" flexWrap="wrap">
          <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.DECREASE.BRANCH')}: {branchName}</Typography>
          <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.DECREASE.DATE_START')}: {toBuddhistYear(moment(rangeDate[0]))}</Typography>
          {rangeDate[1] !== 'Invalid date' && <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.DECREASE.DATE_END')}: {toBuddhistYear(moment(rangeDate[1]))}</Typography>}
          <Typography fontSize="10px" fontWeight={500}>{t('REPORT.FILTER.RECEIPT_STATUS')}: {billStatus === 'ALL' ? t('REPORT.FILTER.ALL') : t(`REPORT.FILTER.BILL.${billStatus}`)}</Typography>
          <Typography fontSize="10px" fontWeight={500}>{t('REPORT.TABLE.INCREASE.CHANNEL_INCREASE')}: {channel === 'ALL' ? t('REPORT.FILTER.ALL') : t(`REPORT.PAYMENT_CHANNEL.${channel}`)}</Typography>
        </Box>

        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '12px', tableLayout: 'auto' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.DF.#NO')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.DECREASE.BRANCH')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.INCREASE.DATE_INCREASE')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.DECREASE.BILL')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.DECREASE.STATUS')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.DECREASE.PATIENT_CN_NUMBER')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.DECREASE.BRANCH_CN_NUMBER')}</th>
              {snType && <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.DECREASE.PATIENT_SN_CODE')}</th>}
              <th style={{ borderBottom: '1px solid black', padding: '4px', textAlign: 'left' }}>{t('REPORT.TABLE.DECREASE.PATIENT_NAME')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.INCREASE.CHANNEL_INCREASE')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px', textAlign: 'right' }}>{t('REPORT.TABLE.INCREASE.TOTAL_INCREASE')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.INCREASE.UPDATED_BY')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px', textAlign: 'left' }}>{t('REPORT.TABLE.INCREASE.DETAIL')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px', textAlign: 'left' }}>{t('REPORT.TABLE.INCREASE.REMARK_CANCEL')}</th>
              <th style={{ borderBottom: '1px solid black', padding: '4px' }}>{t('REPORT.TABLE.INCREASE.CANCELER')}</th>
            </tr>
          </thead>
          <tbody>
            {rowsExport.map((data: any, index: number) => (
              <tr key={index} style={{ fontSize: '9.5px', fontWeight: 500 }}>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{index + 1}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{data?.branchName || '-'}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{dateToView(data.createdAt)}<br />{timeToView(data.createdAt)} น.</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{data?.advanceNumber || '-'}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{t(`REPORT.BILL_STATUS.${data.statusOrder}`)}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{data?.cnNumber || '-'}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{data?.branchCnNumber || '-'}</td>
                {snType && <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{data?.snCode || '-'}</td>}
                <td style={{ textAlign: 'left', verticalAlign: 'top', padding: '4px' }}>{data?.patientFullname || '-'}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{data?.paymentChannel || '-'}</td>
                <td style={{ textAlign: 'right', verticalAlign: 'top', padding: '4px' }}>{numberFormat(data.amountTotal, 2)}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{data?.createdBy || '-'}</td>
                <td style={{ textAlign: 'left', verticalAlign: 'top', padding: '4px', whiteSpace: 'pre-line' }}>{data?.remark ? String(data.remark).replace(/<br\s*\/?>/g, '\n') : '-'}</td>
                <td style={{ textAlign: 'left', verticalAlign: 'top', padding: '4px' }}>{data?.cancelRemark || '-'}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'top', padding: '4px' }}>{data?.cancelBy || '-'}</td>
              </tr>
            ))}
            <tr style={{ backgroundColor: '#E0E0E0', fontSize: '9.5px', fontWeight: 700 }}>
              <td colSpan={snType ? 10 : 9} style={{ textAlign: 'right', padding: '4px' }}>{t('REPORT.TITLE.SUM')}</td>
              <td style={{ textAlign: 'right', padding: '4px' }}>{numberFormat(_.sumBy(rowsExport, 'amountTotal') || 0, 2)}</td>
              <td colSpan={4} />
            </tr>
          </tbody>
        </table>

        <Box marginTop="12px" sx={{ width: '320px' }}>
          <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ fontSize: '9.5px', fontWeight: 700 }}>
                <th style={{ borderBottom: '1px solid black', padding: '4px', textAlign: 'left' }}>{t('REPORT.TABLE.INCREASE.CAP_PAY')}</th>
                <th style={{ borderBottom: '1px solid black', padding: '4px', textAlign: 'right' }}>{t('REPORT.TABLE.INCREASE.SUM_INCREASE')}</th>
                <th style={{ borderBottom: '1px solid black', padding: '4px', textAlign: 'right' }}>{t('REPORT.TABLE.INCREASE.SUM_CANCEL')}</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((row, idx) => (
                <tr key={idx} style={{ fontSize: '9.5px', fontWeight: 500 }}>
                  <td style={{ padding: '4px', textAlign: 'left' }}>{t(row.labelKey)}</td>
                  <td style={{ padding: '4px', textAlign: 'right' }}>{numberFormat(row.total, 2)}</td>
                  <td style={{ padding: '4px', textAlign: 'right' }}>{numberFormat(row.cancel, 2)}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#E0E0E0', fontSize: '9.5px', fontWeight: 700 }}>
                <td style={{ padding: '4px', textAlign: 'left' }}>{t('REPORT.TABLE.INCREASE.SUM_TOTAL')}</td>
                <td style={{ padding: '4px', textAlign: 'right' }}>{numberFormat(sumTotal, 2)}</td>
                <td style={{ padding: '4px', textAlign: 'right' }}>{numberFormat(sumCancel, 2)}</td>
              </tr>
            </tbody>
          </table>
        </Box>
      </Box>

      <Loading show={loading} type="fullLoading" />
    </Box>
  )

}
